import { useEffect, useRef } from 'react';
import {
  ACCESSIBILITY_CDN_BASE_URL,
  ACCESSIBILITY_EQUALWEB_POSITION_STORAGE_KEY,
  ACCESSIBILITY_SCRIPT_PUBLISHER_ATTRIBUTE,
} from '@/constants/accessibility';
import { TemplateType } from '@/types/boot';
import { findAccessibilityButton, watchAccessibilityWidget } from '@/utils/accessibilityWidget';
import { logWarn } from '@/utils/logger';

interface UseAccessibilityWidgetParams {
  /** `checkoutTheme.publisherId` — also the CDN folder the script lives in. */
  publisherId: string | undefined;
  /** The publisher-scoped feature flag. OFF/absent means nothing is injected. */
  enabled: boolean;
  templateType?: TemplateType;
}

/**
 * ACDEV-30609 — mounts the publisher's EqualWeb accessibility widget and makes
 * its icon draggable. Same CDN artifact as the webstore
 * (`accessibility/<publisherId>/accessibility-code.js`), but inlined (some
 * builds need same-document context) and free to move (no fixed corner works
 * across checkout templates; webstore pins bottom-left — ACDEV-28565).
 */
export const useAccessibilityWidget = ({ publisherId, enabled, templateType }: UseAccessibilityWidgetParams): void => {
  const isInjectingRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    // TODO: Currently we don't support accessibility widget for embedded view,
    // as we left it out-of-scope when implementing the feature.
    if (templateType === TemplateType.EMBEDDED_VIEW) return;
    if (!publisherId) {
      return;
    }

    const stopWatching = watchAccessibilityWidget();

    const isAlreadyLoaded =
      Boolean(document.querySelector(`script[${ACCESSIBILITY_SCRIPT_PUBLISHER_ATTRIBUTE}="${publisherId}"]`)) ||
      Boolean(findAccessibilityButton());

    if (!isAlreadyLoaded && !isInjectingRef.current) {
      isInjectingRef.current = true;
      void injectAccessibilityScript(publisherId).catch(() => {
        isInjectingRef.current = false;
      });
    }

    return stopWatching;
  }, [publisherId, enabled, templateType]);
};

const appendInlineAccessibilityScript = (publisherId: string, scriptContent: string): void => {
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.textContent = scriptContent;
  script.setAttribute(ACCESSIBILITY_SCRIPT_PUBLISHER_ATTRIBUTE, publisherId);
  document.body.appendChild(script);
};

const injectAccessibilityScript = async (publisherId: string): Promise<void> => {
  const scriptUrl = `${ACCESSIBILITY_CDN_BASE_URL}/${publisherId}/accessibility-code.js`;

  try {
    const response = await fetch(scriptUrl);
    if (!response.ok) {
      throw new Error(`Accessibility script request failed with ${response.status}`);
    }

    const scriptContent = await response.text();
    if (!scriptContent.trim()) {
      return;
    }

    appendInlineAccessibilityScript(publisherId, scriptContent);
    disableEqualWebDrag();
  } catch (error) {
    const reason = error instanceof Error ? error.message : String(error);
    logWarn('Failed to load accessibility script', { publisherId, scriptUrl, error: reason });
    throw error;
  }
};

interface EqualWebWindow {
  interdeal?: {
    draggable?: boolean;
  };
}

/**
 * Turns off EqualWeb's built-in drag so only ours moves `#INDmenu-btn`.
 * Both handlers fight otherwise (double-counted deltas, window clamp, `top`
 * without `!important`). Plain assignment — EqualWeb does
 * `y.draggable = 0 != y.draggable`, so an accessor would throw. Read once in
 * `InitButtonStyle` after `window.interdeal` exists; set it now to land in time.
 */
const disableEqualWebDrag = (): void => {
  const config = (window as unknown as EqualWebWindow).interdeal;
  if (config) config.draggable = false;
  try {
    localStorage.removeItem(ACCESSIBILITY_EQUALWEB_POSITION_STORAGE_KEY);
  } catch {
    // noop
  }
};
